import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, Users, TrendingUp, Award, Leaf, Handshake, MapPin, Building2,
  ChevronRight, Sparkles, Target, ArrowLeft, ShieldCheck,
} from "lucide-react";

/**
 * National Outcomes & Citizen Impact Centre™ — Phase XIX surface for the
 * National Executive Dashboard. STRICTLY ADDITIVE · ZERO REGRESSION.
 * Every card orchestrates /phase-xix/* endpoints and deep-links to the
 * module that owns the underlying data. No new AI, GIS or PKI service.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const Bar = ({ value, accent }) => (
  <div className="w-full h-1.5 rounded-sm" style={{ background: "rgba(255,255,255,0.08)" }}>
    <div className="h-1.5 rounded-sm" style={{ width: Math.max(0, Math.min(100, value)) + "%", background: accent }} />
  </div>
);

const StatusDot = ({ v }) => {
  const c = v >= 80 ? GREEN : v >= 65 ? AMBER : RED;
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />;
};

const KpiCard = ({ label, value, accent, to, testId, icon: Icon, unit }) => {
  const inner = (
    <div className="rounded-sm border px-2.5 py-2 h-full transition-colors hover:brightness-125"
         style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }}>
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon size={11} style={{ color: accent }} />}
        <div className="font-mono uppercase tracking-[0.16em] text-[9.5px] text-[#7C93A5] flex-1 truncate">{label}</div>
      </div>
      <div className="font-display font-bold text-[15px]" style={{ color: accent }}>
        {unit === "USD" ? `$${Number(value || 0).toLocaleString()}` : (typeof value === "number" ? value.toLocaleString() : (value ?? "—"))}
      </div>
    </div>
  );
  return to ? <Link to={to} data-testid={testId}>{inner}</Link> : <div data-testid={testId}>{inner}</div>;
};

export const NationalOutcomesCentre = () => {
  const [oc, setOc] = useState(null);
  const [ci, setCi] = useState([]);
  const [ben, setBen] = useState(null);
  const [dev, setDev] = useState([]);
  const [vis, setVis] = useState([]);
  const [ti, setTi] = useState(null);
  const [ir, setIr] = useState(null);
  const [ei, setEi] = useState([]);
  const [si, setSi] = useState([]);
  const [eo, setEo] = useState([]);
  const [imp, setImp] = useState([]);
  const [intl, setIntl] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const paths = [
          "phase-xix/outcomes-centre","phase-xix/citizen-impact",
          "phase-xix/benefits-realisation","phase-xix/development-impact",
          "phase-xix/vision-alignment","phase-xix/transformation-index",
          "phase-xix/investment-realisation","phase-xix/economic-impact",
          "phase-xix/social-impact","phase-xix/environmental-outcomes",
          "phase-xix/impact-scorecard","phase-xix/international-outcomes",
        ];
        const r = await Promise.all(paths.map((p) => axios.get(`${API}/cadastre360/${p}`, { headers: auth() })));
        setOc(r[0].data); setCi(r[1].data.indicators || []);
        setBen(r[2].data); setDev(r[3].data.pillars || []);
        setVis(r[4].data.frameworks || []); setTi(r[5].data);
        setIr(r[6].data); setEi(r[7].data.estimates || []);
        setSi(r[8].data.categories || []); setEo(r[9].data.metrics || []);
        setImp(r[10].data.scorecard || []); setIntl(r[11].data.indicators || []);
      } catch (e) {
        toast.error("Outcomes Centre load failed — " + (e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  return (
    <div className="p-6 space-y-4" data-testid="page-national-outcomes-centre">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: GREEN + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GREEN}30 100%)` }}>
        <Users size={38} className="mt-1" style={{ color: GREEN }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GREEN }}>
            Cadastre360™ · National Outcomes · Citizen Impact
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            National Outcomes Centre™
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Beyond outputs — measure real citizen benefit, economic transformation, environmental restoration
            and Vision 2030 alignment. Reuses existing AI narrative and GIS engines. No new services introduced.
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link to="/app/national-executive-dashboard" data-testid="btn-back-ned"
                className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                style={{ borderColor: GOLD + "70", color: "#071B2B", background: GOLD }}>
            <ArrowLeft size={13} /> Executive Dashboard
          </Link>
          {ti && (
            <div className="rounded-sm border px-2 py-1 flex items-center gap-2"
                 style={{ borderColor: GREEN + "60", background: "rgba(7,27,43,0.55)" }}>
              <Sparkles size={13} style={{ color: GREEN }} />
              <div className="font-mono uppercase text-[9.5px] tracking-[0.14em]" style={{ color: GREEN }}>
                National Transformation Index · {ti.overall}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Outcomes indicators (headline) */}
      <Card title="National Outcomes Indicators™" accent={GREEN} testId="noc-outcomes"
            right={<Chip color={GREEN}>{oc?.indicators?.length || 0} indicators</Chip>}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {(oc?.indicators || []).map((i, idx) => (
            <KpiCard key={i.label} label={i.label} value={i.score} accent={idx % 2 ? GREEN : TEAL}
                      icon={TrendingUp} testId={`noc-ind-${idx}`} />
          ))}
        </div>
      </Card>

      {/* Citizen Impact + Vision 2030 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Citizen Impact Index™" accent={PURPLE} testId="noc-citizen">
          <div className="space-y-1.5 max-h-[380px] overflow-auto">
            {ci.map((r, idx) => (
              <div key={idx} className="rounded-sm border px-2 py-1.5"
                   style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}
                   data-testid={`noc-citizen-${idx}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <StatusDot v={r.current} />
                  <div className="text-[11.5px] text-[#F6F8FA] flex-1">{r.indicator}</div>
                  <span className="font-mono text-[11px]" style={{ color: PURPLE }}>{r.current}</span>
                </div>
                <Bar value={r.current} accent={PURPLE} />
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  {r.province} · {r.ministry} · Target {r.target} · Forecast {r.forecast}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Vision 2030 & Framework Alignment" accent={GOLD} testId="noc-vision">
          <div className="space-y-1.5">
            {vis.map((f) => (
              <div key={f.framework} className="rounded-sm border px-2 py-1.5"
                   style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <Target size={11} style={{ color: GOLD }} />
                  <div className="text-[11.5px] text-[#F6F8FA] flex-1">{f.framework}</div>
                  <Chip color={GOLD} filled>{f.contribution_pct}%</Chip>
                </div>
                <Bar value={f.progress_pct} accent={GOLD} />
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  Progress {f.progress_pct}% · Gap {f.gap_pct}% · Target {f.expected_completion}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Benefits + Transformation Index */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Benefits Realisation Engine™" accent={GREEN} testId="noc-benefits">
          {ben ? (
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ["Revenue Generated",      "$" + Number(ben.revenue_generated_usd || 0).toLocaleString()],
                ["Revenue Protected",      "$" + Number(ben.revenue_protected_usd).toLocaleString()],
                ["Investment Realised",    "$" + Number(ben.investment_realised_usd).toLocaleString()],
                ["Projects Completed",     Number(ben.projects_completed).toLocaleString()],
                ["Productivity",           ben.productivity_improvement_pct + "%"],
                ["Processing Time Δ",      ben.processing_time_reduction_pct + "%"],
                ["Env CO₂ Reduced",        Number(ben.environmental_benefits_tonnes_co2).toLocaleString() + " t"],
                ["Admin Efficiency",       ben.administrative_efficiency_pct + "%"],
                ["Economic Multiplier",    "×" + ben.economic_multiplier],
                ["ROGI",                   ben.rogi_pct + "%"],
                ["GDP Contribution",       "$" + Number(ben.estimated_gdp_contribution_usd).toLocaleString()],
                ["Employment Multiplier",  "×" + ben.estimated_employment_multiplier],
              ].map(([l, v]) => (
                <div key={l} className="rounded-sm border px-2 py-1"
                     style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9px] tracking-[0.14em] text-[#7C93A5] truncate">{l}</div>
                  <div className="text-[12.5px] font-bold" style={{ color: GREEN }}>{v}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="National Transformation Index™" accent={PURPLE} testId="noc-transformation">
          {ti ? (
            <div className="space-y-1.5 max-h-[380px] overflow-auto">
              {ti.dimensions.map((d) => (
                <div key={d.dimension}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className="text-[11px] text-[#F6F8FA] flex-1 truncate">{d.dimension}</div>
                    <span className="font-mono text-[11px] text-[#F6F8FA]">{d.score}</span>
                  </div>
                  <Bar value={d.score} accent={PURPLE} />
                </div>
              ))}
              <div className="pt-1 text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.14em]">
                Composite Transformation Score · {ti.overall}
              </div>
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      {/* Development Impact Pillars */}
      <Card title="National Development Impact™ · pillars" accent={BLUE} testId="noc-dev-impact">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {dev.map((p) => (
            <div key={p.pillar} className="rounded-sm border px-2 py-1.5"
                 style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="flex items-center gap-2 mb-1">
                <StatusDot v={p.overall} />
                <div className="text-[11.5px] text-[#F6F8FA] flex-1">{p.pillar}</div>
                <span className="font-mono text-[11px]" style={{ color: BLUE }}>{p.overall}</span>
              </div>
              <Bar value={p.overall} accent={BLUE} />
              <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                Forecast {p.forecast} · Target {p.target} · Risk {p.risk}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Economic Impact + Investment Realisation + Social Impact */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Economic Impact Modeller" accent={GOLD} testId="noc-econ">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {ei.map((r) => (
              <div key={r.estimate} className="rounded-sm border px-2 py-1"
                   style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="text-[11px] text-[#F6F8FA]">{r.estimate}</div>
                <div className="text-[9.5px] text-[#7C93A5]">
                  Base {r.baseline} · Now {r.current} · Projected {r.projected}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Investment Realisation Monitor" accent={PURPLE} testId="noc-invest">
          {ir ? (
            <div className="space-y-1">
              {ir.stages.map((s) => (
                <div key={s.stage} className="rounded-sm border px-2 py-1"
                     style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] text-[#F6F8FA] flex-1">{s.stage}</div>
                    <span className="font-mono text-[11px]" style={{ color: PURPLE }}>${(s.value_usd/1_000_000).toFixed(0)}M</span>
                  </div>
                  <div className="text-[9.5px] text-[#7C93A5]">{s.count} projects</div>
                </div>
              ))}
              <div className="pt-1 text-[10px] text-[#7C93A5]">
                Total ${(ir.totals.capital_value_usd/1_000_000).toFixed(0)}M · Jobs {Number(ir.totals.jobs).toLocaleString()}
              </div>
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="Social Impact Centre" accent={GREEN} testId="noc-social">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {si.map((c) => (
              <div key={c.category} className="rounded-sm border px-2 py-1"
                   style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{c.category}</div>
                  <span className="font-mono text-[11px]" style={{ color: GREEN }}>{c.score}</span>
                </div>
                <Bar value={c.score} accent={GREEN} />
                <div className="mt-0.5 text-[9.5px] text-[#7C93A5]">{c.programmes} programmes · {c.responsible_ministry}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Environmental Outcomes + Impact Scorecard + International */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Environmental Outcomes Centre" accent={TEAL} testId="noc-env">
          <div className="grid grid-cols-1 gap-1 max-h-[300px] overflow-auto">
            {eo.map((m) => (
              <div key={m.label} className="rounded-sm border px-2 py-1 flex items-center gap-2"
                   style={{ borderColor: TEAL + "30", background: "rgba(7,27,43,0.5)" }}>
                <Leaf size={11} style={{ color: TEAL }} />
                <div className="text-[11px] text-[#F6F8FA] flex-1">{m.label}</div>
                <span className="font-mono text-[11px]" style={{ color: TEAL }}>{Number(m.value).toLocaleString()} {m.unit}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Executive Impact Scorecard™" accent={GOLD} testId="noc-scorecard">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {imp.map((r) => (
              <div key={r.row} className="rounded-sm border px-2 py-1"
                   style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{r.row}</div>
                  <span className="font-mono text-[11px]" style={{ color: GOLD }}>{r.current}</span>
                </div>
                <Bar value={r.current} accent={GOLD} />
                <div className="mt-0.5 text-[9.5px] text-[#7C93A5]">Forecast {r.forecast} · Intl bench {r.international_benchmark}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="International Comparative Outcomes" accent={BLUE} testId="noc-intl">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {intl.map((i) => (
              <div key={i.indicator} className="rounded-sm border px-2 py-1"
                   style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Building2 size={11} style={{ color: BLUE }} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{i.indicator}</div>
                  {i.status === "data_available" ? (
                    <span className="font-mono text-[11px]" style={{ color: BLUE }}>{i.our_score}</span>
                  ) : (
                    <Chip color={AMBER}>pending</Chip>
                  )}
                </div>
                {i.status === "data_available" && (
                  <div className="text-[9.5px] text-[#7C93A5]">
                    Peers avg {i.peer_average} · Intl avg {i.international_average}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        National Outcomes Centre™ · Phase XIX orchestration · outputs vs outcomes · zero regression
      </div>
    </div>
  );
};

export default NationalOutcomesCentre;
